import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormGroup
} from '@angular/forms';
import { OpportunityPropertyDefinition } from '../models/opportunity-property-definition';

export type PropertyUpdate = {
  // These properties are prefixed with an underscore, to mitigate the risk of mistaken identity in the context of a
  // nested PropertyUpdateStructure. The logic in the functions below needs to be able to identify whether a value is a
  // PropertyUpdate or a PropertyUpdateStructure. However, a PropertyUpdateStructure does not have a pre-defined set of
  // properties, so there is a risk of a clash, if a part of the structure happens to contain fields named 'change' and
  // 'value'. To reduce that risk, the definition of PropertyUpdate has been made unique.
  _change: 'Update' | 'Remove';
  _value: any | null;
};

export type PropertyUpdateStructure = {
  [key: string]: PropertyUpdateStructure | PropertyUpdate | null;
};

function isPropertyUpdate(x: any): x is PropertyUpdate {
  return x != null && x != undefined && '_change' in x && '_value' in x;
}

export function propertySet(value: any): PropertyUpdate {
  return {
    _change: 'Update',
    _value: value
  };
}

export const propertyRemove: PropertyUpdate = {
  _change: 'Remove',
  _value: null
};

export function cvaPropertyUpdate<T>(
  property: OpportunityPropertyDefinition<T>,
  conversion: (x: any) => any
): PropertyUpdate | null {
  return property.change == undefined
    ? null
    : {
        _change: property.change,
        _value: conversion(property.value)
      };
}

export function propertyUpdate<T>(
  control: AbstractControl,
  conversion: (x: any) => T,
  defaultValue: T | null = null
): PropertyUpdate | null {
  return control.dirty
    ? {
        _change: control.value === '' ? 'Remove' : 'Update',
        _value: control.value === '' ? null : conversion(control.value)
      }
    : defaultValue != null
    ? propertySet(defaultValue)
    : null;
}

export function propertiesUpdates(
  controls: AbstractControl[],
  key: (i: number) => string,
  conversion: (x: any) => any
): PropertyUpdateStructure {
  let updates: { [key: string]: PropertyUpdate | null } = {};
  controls.forEach(
    (control, i) => (updates[key(i)] = propertyUpdate(control, conversion))
  );
  return updates;
}

export function collectionUpdates(
  forms: UntypedFormArray,
  removals: string[],
  key: (i: number) => string,
  itemUpdates: (form: UntypedFormGroup, i: number) => PropertyUpdateStructure
): PropertyUpdateStructure {
  let updates: PropertyUpdateStructure = {};
  forms.controls.forEach((x, i) => {
    if (x instanceof UntypedFormGroup) {
      updates[key(i)] = itemUpdates(x as UntypedFormGroup, i);
    }
  });
  removals.forEach((id) => (updates[id] = propertyRemove));
  return updates;
}

export function flatCollectionUpdates(
  forms: UntypedFormArray,
  removals: string[],
  key: (form: UntypedFormGroup) => string,
  itemUpdates: (form: UntypedFormGroup, i: number) => PropertyUpdate | null
): PropertyUpdateStructure {
  let updates: PropertyUpdateStructure = {};
  forms.controls.forEach((x, i) => {
    if (x instanceof UntypedFormGroup) {
      let form = x as UntypedFormGroup;
      updates[key(form)] = itemUpdates(form, i);
    }
  });
  removals.forEach((id) => (updates[id] = propertyRemove));
  return updates;
}

export function arrayCollectionUpdates<T>(
  forms: UntypedFormArray,
  itemUpdate: (form: UntypedFormGroup, i: number) => T | null
): PropertyUpdate | null {
  let result: T[] = [];
  let dirty = false;

  forms.controls.forEach((x, i) => {
    if (x instanceof UntypedFormGroup) {
      let form = x as UntypedFormGroup;
      let value = itemUpdate(form, i);
      if (value != null) result.push(value);
      if (form.dirty) dirty = true;
    }
  });

  return dirty
    ? {
        _change: result.length == 0 ? 'Remove' : 'Update',
        _value: result
      }
    : null;
}

export function propertyUpdates(x: PropertyUpdateStructure): {
  [key: string]: any;
} {
  let updates: { [key: string]: any } = {};
  for (let key in x) {
    const update: PropertyUpdateStructure | PropertyUpdate | null = x[key];
    if (update != null) {
      if (isPropertyUpdate(update)) {
        switch (update._change) {
          case 'Remove':
            updates[key] = null;
            break;
          case 'Update':
            updates[key] = update._value;
            break;
        }
      } else {
        const structure = propertyUpdates(update);
        if (Object.keys(structure).length > 0) {
          updates[key] = structure;
        }
      }
    }
  }
  return updates;
}
